import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Divider,
  Alert,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

const CodeBlock = ({ code, language = 'javascript' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        bgcolor: '#1e1e1e',
        p: 2,
        borderRadius: 1,
        overflow: 'auto',
      }}
    >
      <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'grey.500',
            '&:hover': { color: 'white' },
          }}
        >
          {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{
          m: 0,
          color: '#d4d4d4',
          fontFamily: 'Monaco, Consolas, monospace',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {code}
      </Box>
    </Paper>
  );
};

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const ApiKeyDocs = ({ open, onClose, apiEndpoint }) => {
  const [tab, setTab] = useState(0);

  const baseUrl = apiEndpoint || window.location.origin;

  const htmlExample = `<!DOCTYPE html>
<html>
<head>
  <title>Contact Form</title>
</head>
<body>
  <form id="contactForm">
    <input type="text" name="name" placeholder="Your Name" required>
    <input type="email" name="email" placeholder="Email" required>
    <input type="tel" name="phone" placeholder="Phone">
    <textarea name="message" placeholder="Message"></textarea>
    <button type="submit">Send</button>
  </form>

  <script>
    const API_KEY = 'YOUR_API_KEY_HERE';
    const API_URL = '${baseUrl}/api/public/forms/submit';

    document.getElementById('contactForm').addEventListener('submit', async (e) => {
      e.preventDefault();

      const formData = Object.fromEntries(new FormData(e.target));

      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': API_KEY
          },
          body: JSON.stringify(formData)
        });

        const result = await response.json();

        if (result.success) {
          alert('Thank you! We will be in touch soon.');
          e.target.reset();
        } else {
          alert('Error: ' + (result.errors?.join(', ') || result.message));
        }
      } catch (error) {
        alert('Failed to submit form. Please try again.');
      }
    });
  </script>
</body>
</html>`;

  const reactExample = `import React, { useState } from 'react';

const API_KEY = 'YOUR_API_KEY_HERE';
const API_URL = '${baseUrl}/api/public/forms/submit';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState({ loading: false, error: null, success: false });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: null, success: false });

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setStatus({ loading: false, error: null, success: true });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus({
          loading: false,
          error: result.errors?.join(', ') || result.message,
          success: false
        });
      }
    } catch (error) {
      setStatus({ loading: false, error: 'Failed to submit form', success: false });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <input name="name" value={formData.name} onChange={handleChange} placeholder="Name" required />
      <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email" required />
      <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone" />
      <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Message" />

      <button type="submit" disabled={status.loading}>
        {status.loading ? 'Sending...' : 'Send'}
      </button>

      {status.error && <p style={{ color: 'red' }}>{status.error}</p>}
      {status.success && <p style={{ color: 'green' }}>Message sent successfully!</p>}
    </form>
  );
};

export default ContactForm;`;

  const curlExample = `# Submit a contact form
curl -X POST ${baseUrl}/api/public/forms/submit \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY_HERE" \\
  -d '{
    "name": "John Doe",
    "email": "john@example.com",
    "phone": "555-1234",
    "message": "I would like to learn more about your services."
  }'

# Success Response:
# { "success": true, "message": "Form submitted successfully" }

# Error Response (missing required field):
# { "success": false, "message": "Validation failed", "errors": ["Email is required"] }

# Error Response (invalid API key):
# { "success": false, "message": "Invalid or inactive API key." }

# Error Response (origin not allowed):
# { "success": false, "message": "Origin not allowed for this API key." }`;

  const phpExample = `<?php
$apiKey = 'YOUR_API_KEY_HERE';
$apiUrl = '${baseUrl}/api/public/forms/submit';

// Collect form data
$data = [
    'name' => $_POST['name'] ?? '',
    'email' => $_POST['email'] ?? '',
    'phone' => $_POST['phone'] ?? '',
    'message' => $_POST['message'] ?? ''
];

// Send request
$ch = curl_init($apiUrl);
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_HTTPHEADER => [
        'Content-Type: application/json',
        'X-API-Key: ' . $apiKey
    ],
    CURLOPT_RETURNTRANSFER => true
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$result = json_decode($response, true);

if ($result['success']) {
    echo "Thank you! Your message has been sent.";
} else {
    echo "Error: " . ($result['message'] ?? 'Unknown error');
}
?>`;

  const wordpressExample = `<?php
/**
 * Add this to your theme's functions.php or a custom plugin
 */

// Register shortcode [tedora_contact_form]
add_shortcode('tedora_contact_form', 'tedora_contact_form_shortcode');

function tedora_contact_form_shortcode() {
    ob_start();
    ?>
    <form id="tedora-contact-form" class="tedora-form">
        <p>
            <label>Name *</label>
            <input type="text" name="name" required>
        </p>
        <p>
            <label>Email *</label>
            <input type="email" name="email" required>
        </p>
        <p>
            <label>Phone</label>
            <input type="tel" name="phone">
        </p>
        <p>
            <label>Message</label>
            <textarea name="message" rows="4"></textarea>
        </p>
        <p>
            <button type="submit">Send Message</button>
        </p>
        <div class="form-message"></div>
    </form>

    <script>
    document.getElementById('tedora-contact-form').addEventListener('submit', async function(e) {
        e.preventDefault();

        const form = e.target;
        const messageDiv = form.querySelector('.form-message');
        const submitBtn = form.querySelector('button[type="submit"]');

        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';

        const formData = Object.fromEntries(new FormData(form));

        try {
            const response = await fetch('${baseUrl}/api/public/forms/submit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': 'YOUR_API_KEY_HERE'
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                messageDiv.innerHTML = '<p style="color: green;">Thank you! We will contact you soon.</p>';
                form.reset();
            } else {
                messageDiv.innerHTML = '<p style="color: red;">Error: ' + (result.message || 'Please try again.') + '</p>';
            }
        } catch (error) {
            messageDiv.innerHTML = '<p style="color: red;">Failed to send. Please try again later.</p>';
        }

        submitBtn.disabled = false;
        submitBtn.textContent = 'Send Message';
    });
    </script>
    <?php
    return ob_get_clean();
}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight={700}>
          API Integration Documentation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Learn how to integrate contact forms with your website
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Overview Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Overview
          </Typography>
          <Typography variant="body2" paragraph>
            The Contact Form API allows you to collect form submissions from your external websites
            and automatically store them in your CRM. Each API key can have custom field configurations
            to match your specific form requirements.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Endpoint:</strong> <code>POST {baseUrl}/api/public/forms/submit</code>
          </Alert>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Setup Steps */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Setup Steps
          </Typography>

          <Box sx={{ pl: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              1. Create an API Key
            </Typography>
            <Typography variant="body2" paragraph sx={{ pl: 2 }}>
              Go to Settings → API Keys → Create API Key. Give it a descriptive name
              (e.g., "Marketing Website Contact Form").
            </Typography>

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              2. Configure Allowed Origins
            </Typography>
            <Typography variant="body2" paragraph sx={{ pl: 2 }}>
              Add the domains that will be sending form submissions (e.g., <code>https://yourwebsite.com</code>).
              This prevents unauthorized websites from using your API key.
            </Typography>

            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              3. Define Field Mappings
            </Typography>
            <Typography variant="body2" paragraph sx={{ pl: 2 }}>
              Configure the fields your form will send. Each field has:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mt: 0 }}>
              <li><Typography variant="body2"><strong>External Field Name:</strong> The key in your form data (e.g., <code>full_name</code>)</Typography></li>
              <li><Typography variant="body2"><strong>Display Name:</strong> How it appears in the CRM (e.g., "Full Name")</Typography></li>
              <li><Typography variant="body2"><strong>Field Type:</strong> For validation - text, email, phone, textarea, number, or date</Typography></li>
              <li><Typography variant="body2"><strong>Required:</strong> Whether the field must be present</Typography></li>
            </Box>

            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              4. Copy Your API Key
            </Typography>
            <Typography variant="body2" paragraph sx={{ pl: 2 }}>
              After creating, copy the API key immediately - it won't be shown again!
              Store it securely and use it in your form's code.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Request Format */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Request Format
          </Typography>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Headers
          </Typography>
          <CodeBlock code={`Content-Type: application/json
X-API-Key: your_api_key_here`} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
            Request Body
          </Typography>
          <Typography variant="body2" paragraph>
            Send a JSON object with keys matching your configured field mappings:
          </Typography>
          <CodeBlock code={`{
  "name": "John Doe",
  "email": "john@example.com",
  "phone": "555-123-4567",
  "message": "I'm interested in your services."
}`} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
            Response Format
          </Typography>
          <CodeBlock code={`// Success (201 Created)
{ "success": true, "message": "Form submitted successfully" }

// Validation Error (400 Bad Request)
{ "success": false, "message": "Validation failed", "errors": ["Email is required", "Email must be a valid email address"] }

// Invalid API Key (401 Unauthorized)
{ "success": false, "message": "Invalid or inactive API key." }

// Origin Not Allowed (403 Forbidden)
{ "success": false, "message": "Origin not allowed for this API key." }

// Rate Limited (429 Too Many Requests)
{ "success": false, "message": "Rate limit exceeded. Please try again later." }`} />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Code Examples */}
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Code Examples
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab label="HTML/JavaScript" />
              <Tab label="React" />
              <Tab label="cURL" />
              <Tab label="PHP" />
              <Tab label="WordPress" />
            </Tabs>
          </Box>

          <TabPanel value={tab} index={0}>
            <Typography variant="body2" paragraph>
              Basic HTML form with vanilla JavaScript:
            </Typography>
            <CodeBlock code={htmlExample} />
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Typography variant="body2" paragraph>
              React component with state management:
            </Typography>
            <CodeBlock code={reactExample} />
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Typography variant="body2" paragraph>
              Test your API key using cURL:
            </Typography>
            <CodeBlock code={curlExample} />
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <Typography variant="body2" paragraph>
              PHP backend form handler:
            </Typography>
            <CodeBlock code={phpExample} />
          </TabPanel>

          <TabPanel value={tab} index={4}>
            <Typography variant="body2" paragraph>
              WordPress shortcode for contact forms:
            </Typography>
            <CodeBlock code={wordpressExample} />
          </TabPanel>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Security Notes */}
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Security Best Practices
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Important:</strong> Never expose your API key in public repositories or client-side code
            that could be easily inspected. While the key is designed for client-side use, always configure
            allowed origins to restrict which domains can use it.
          </Alert>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <li><Typography variant="body2">Always configure allowed origins for production API keys</Typography></li>
            <li><Typography variant="body2">Use HTTPS for all form submissions</Typography></li>
            <li><Typography variant="body2">Regenerate API keys if you suspect they've been compromised</Typography></li>
            <li><Typography variant="body2">Monitor usage statistics to detect unusual activity</Typography></li>
            <li><Typography variant="body2">Rate limiting is enforced at 100 requests per minute per IP</Typography></li>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyDocs;
